import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import CheckoutModal from "@/components/CheckoutModal";
import { drawerOverlay, drawerPanel } from "@/lib/motion-presets";

const CartDrawer = () => {
  const { open, setOpen, list, count, subtotal, update, remove } = useCart();
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  return (
    <>
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              {...drawerOverlay}
              onClick={() => setOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
              data-testid="cart-overlay"
            />
            <motion.aside
              {...drawerPanel}
              className="fixed top-0 right-0 bottom-0 w-full sm:w-[460px] z-50 flex flex-col"
              style={{ background: "var(--awd-surface)", borderLeft: "1px solid var(--awd-border)" }}
              data-testid="cart-drawer"
            >
              <div className="kente-stripe" />
              <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: "var(--awd-border)" }}>
                <div>
                  <div className="eyebrow">Your Cart</div>
                  <div className="font-display text-2xl">{count} {count === 1 ? "item" : "items"}</div>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="p-2 hover:bg-[#231e1c] transition-colors"
                  data-testid="cart-close-button"
                ><X size={20} /></button>
              </div>

              <div className="flex-grow overflow-y-auto px-6 py-4">
                {list.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="cart-empty">
                    <ShoppingBag size={48} style={{ color: "var(--awd-border-2)" }} />
                    <div className="font-display text-xl mt-4" style={{ color: "var(--awd-text-2)" }}>Your cart is empty</div>
                    <p className="text-sm mt-2 max-w-xs" style={{ color: "var(--awd-text-3)" }}>
                      Add a plate from the menu to start your pre-order.
                    </p>
                    <button onClick={() => setOpen(false)} className="btn-ghost mt-6">Browse Menu</button>
                  </div>
                )}

                {list.map(({ item, qty }) => (
                  <div key={item.id} className="flex gap-4 py-5 border-b" style={{ borderColor: "var(--awd-border)" }} data-testid={`cart-item-${item.id}`}>
                    <img src={item.image} alt={item.name} className="w-20 h-20 object-cover" />
                    <div className="flex-grow">
                      <div className="flex justify-between gap-2">
                        <h4 className="font-display text-lg">{item.name}</h4>
                        <button onClick={() => remove(item.id)} className="text-[#756B65] hover:text-[#9B2226]" data-testid={`remove-${item.id}`}><Trash2 size={16} /></button>
                      </div>
                      <div className="text-sm mt-1" style={{ color: "var(--awd-text-3)" }}>${item.price.toFixed(2)} each</div>
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gap-2">
                          <button className="qty-btn" onClick={() => update(item.id, qty - 1)} data-testid={`dec-${item.id}`}><Minus size={14} /></button>
                          <span className="font-display text-lg w-6 text-center" data-testid={`qty-${item.id}`}>{qty}</span>
                          <button className="qty-btn" onClick={() => update(item.id, qty + 1)} data-testid={`inc-${item.id}`}><Plus size={14} /></button>
                        </div>
                        <div className="font-display text-lg" style={{ color: "var(--awd-ochre)" }}>${(item.price * qty).toFixed(2)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {list.length > 0 && (
                <div className="p-6 border-t" style={{ borderColor: "var(--awd-border)" }}>
                  <div className="flex justify-between mb-1">
                    <span className="label-mini">Subtotal</span>
                    <span className="font-display text-2xl">${subtotal.toFixed(2)}</span>
                  </div>
                  <p className="text-xs mb-5" style={{ color: "var(--awd-text-3)" }}>
                    Tax included. No pickup fee for soft launch.
                  </p>
                  <button
                    className="btn-primary w-full"
                    onClick={() => setCheckoutOpen(true)}
                    data-testid="checkout-button"
                  >
                    Checkout · ${subtotal.toFixed(2)}
                  </button>
                </div>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <CheckoutModal open={checkoutOpen} onClose={() => setCheckoutOpen(false)} />
    </>
  );
};

export default CartDrawer;
